// @flow
import React from "react";
import styled from "styled-components";
import { find } from "lodash";
import Downshift from "downshift";
import { Chevron } from "@nested/ui/icons";
import { Label } from "./Label";

export const ArrowContainer = styled.div`
  position: relative;
`;

export const InputArrow = styled(Chevron)`
  right: 18px;
  position: absolute;
  top: 18px;
  width: 18px;
  cursor: pointer;
  pointer-events: none;
`;

export const Container = styled.div`
  position: relative;
  margin-bottom: 20px;
`;

export const Options = styled.ul`
  background-color: white;
  border-radius: 0px 0px 4px 4px;
  border: 1px solid #f5f5f5;
  border-top: none;
  box-shadow: 0 10px 10px -10px rgba(0, 0, 0, 0.05);
  list-style-type: none;
  margin: 0px;
  overflow-y: auto;
  padding-top: 4px;
  padding: 15px 10px;
  transition: max-height 250ms ease-out, visibility 250ms ease-out;
  width: 100%;
  box-sizing: border-box;
  position: absolute;
  z-index: 999;
  top: calc(100% - 10px);
  max-height: 250px;
`;

export const Option = styled.li`
  cursor: pointer;
  border: 1px solid transparent;
  font-weight: 400;
  color: ${({ theme }) => theme.palette.hague};
  padding: 10px;
  margin: 0px;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
  display: block;
  font-size: 16px;
`;

export const Input = styled.input`
  display: block;
  border-radius: 4px;
  border: 1px solid #f5f5f5;
  cursor: pointer;
  background-color: white;
  box-shadow: 0 10px 10px -10px rgba(0, 0, 0, 0.05);
  color: ${({ theme }) => theme.palette.hague};
  font-size: 16px;
  font-weight: 400;
  line-height: 32px;
  overflow: hidden;
  padding: 10px 35px 10px 20px;
  position: relative;
  text-overflow: ellipsis;
  white-space: nowrap;
  width: 100%;
  box-sizing: border-box;

  ::placeholder {
    opacity: 0.6;
  }
`;

export type Item<T> = {
  label: string,
  value: T,
};

export type Props<T> = {
  className?: string,
  dataTest?: string,
  "data-test"?: string,
  value: T,
  options: Item<T>[],
  label?: string,
  onChange(item: Item<T>): void,
  placeholder?: string,
  id?: string,
};

export type State = {
  menuOpen: boolean,
};

export class Select<T> extends React.Component<Props<T>, State> {
  state = {
    menuOpen: false,
  };

  openMenu = () => {
    this.setState({ menuOpen: true });
  };

  closeMenu = () => {
    this.setState({ menuOpen: false });
  };

  toggleMenu = () => {
    this.setState(({ menuOpen }) => ({
      menuOpen: !menuOpen,
    }));
  };

  render() {
    const {
      className,
      "data-test": test,
      dataTest = test || "select",
      value,
      options,
      label,
      onChange,
      placeholder,
      id,
    } = this.props;
    const { menuOpen } = this.state;
    return (
      <Downshift
        // Using undefined here causes warnings from Downshift so we default to null instead
        selectedItem={find(options, (item) => item.value === value) || null}
        itemToString={(item) => (item ? item.label : "")}
        onChange={onChange}
        isOpen={menuOpen}
        id={id}
      >
        {({
          getInputProps,
          getItemProps,
          getLabelProps,
          getMenuProps,
          getRootProps,
          isOpen,
          highlightedIndex,
          selectedItem,
        }) => (
          <Container
            className={className}
            {...getRootProps({ refKey: "selectRef", suppressRefError: true })}
            onClick={this.toggleMenu}
          >
            {label && <Label {...getLabelProps()}>{label}</Label>}
            <ArrowContainer>
              <Input
                {...getInputProps({ onBlur: this.closeMenu })}
                placeholder={placeholder}
                readOnly
                data-test={`${dataTest}-input`}
              />
              <InputArrow inverted={isOpen} />
            </ArrowContainer>
            {isOpen ? (
              <Options {...getMenuProps()}>
                {options.map((item, index) => (
                  <Option
                    {...getItemProps({
                      key: index,
                      index,
                      item,
                      style: {
                        backgroundColor:
                          highlightedIndex === index ? "#FAF7F4" : "",
                        fontWeight: selectedItem === item ? "500" : "400",
                        border:
                          highlightedIndex === index ? "solid 1px #F2EBE3" : "",
                      },
                    })}
                    data-test={`${dataTest}-option-${index}`}
                  >
                    {item.label}
                  </Option>
                ))}
              </Options>
            ) : null}
          </Container>
        )}
      </Downshift>
    );
  }
}
